// Mensagens amigáveis para a pessoa ler, no lugar de erros técnicos do banco.
// Testa PRIMEIRO os padrões técnicos conhecidos; só depois deixa passar a
// mensagem do banco (que, quando é amigável, já vem em português).
//
// Regra de ouro: erro técnico NUNCA chega cru à tela. O detalhe técnico fica
// apenas no console, para quem estiver cuidando do suporte.

interface ErroSupabase {
  message?: string;
  code?: string;
  details?: string;
  hint?: string;
  error_description?: string;
  status?: number;
}

// A frase única para qualquer falha técnica inesperada.
export const MENSAGEM_ERRO_TECNICO =
  'Não consegui fazer isso agora. Confira a internet e tente de novo em instantes.';

function extrairMensagem(e: unknown): string {
  if (typeof e === 'string') return e;
  if (e instanceof Error) return e.message;
  if (typeof e === 'object' && e !== null) {
    const o = e as ErroSupabase;
    return o.message || o.error_description || o.details || o.hint || '';
  }
  return '';
}

function extrairCodigo(e: unknown): string {
  if (typeof e === 'object' && e !== null && 'code' in e) {
    return String((e as { code?: unknown }).code ?? '');
  }
  return '';
}

// Registra o erro de verdade no console (nunca na tela).
function registrarTecnico(e: unknown, msg: string, codigo: string) {
  try {
    // eslint-disable-next-line no-console
    console.error('[erro]', { mensagem: msg, codigo, original: e });
  } catch {
    /* console indisponível: segue em frente */
  }
}

export function mensagemAmigavel(e: unknown, padrao = MENSAGEM_ERRO_TECNICO): string {
  const msg = extrairMensagem(e);
  const codigo = extrairCodigo(e).toLowerCase();
  const m = msg.toLowerCase();

  if (!msg && !codigo) return padrao;

  // Edge Functions / chamadas de rede: sempre a frase única.
  if (
    /edge function|functionshttperror|non-2xx|non 2xx|function returned a non|functionsrelayerror|functionsfetcherror/i.test(
      m,
    )
  ) {
    registrarTecnico(e, msg, codigo);
    return MENSAGEM_ERRO_TECNICO;
  }

  // Falhas de rede e fetch (inclui "Failed to fetch", "Load failed", "NetworkError").
  if (
    /failed to fetch|load failed|networkerror|network request failed|err_network|err_internet|network|fetch failed|connection|unreachable|timeout|offline/i.test(
      m,
    )
  ) {
    registrarTecnico(e, msg, codigo);
    return 'Sem conexão. Verifique sua internet e tente de novo.';
  }

  // Sessão/credenciais.
  if (/jwt|not authenticated|invalid token|token.*expired|invalid_grant|pgrst301|refresh token/i.test(m)) {
    registrarTecnico(e, msg, codigo);
    return 'Sua sessão expirou. Entre de novo para continuar.';
  }

  // P0001 = RAISE EXCEPTION das regras de negócio (ex.: "As inscrições para
  // esta participação já encerraram."). A frase já vem pronta, em português,
  // escrita para a pessoa ler — então passa direto, nunca vira a genérica.
  if (codigo === 'p0001' && msg) return msg;

  // Padrões técnicos do banco — viram frases em português.
  if (/duplicate key/i.test(m)) return 'Já existe um registro igual.';
  if (/row-level security|permission denied/i.test(m)) return 'Você não tem permissão para isso.';
  if (codigo === 'pgrst116' || /pgrst116/i.test(m)) return 'Não encontrado.';
  if (/invalid input syntax/i.test(m)) return 'Confira os campos preenchidos e tente de novo.';
  if (/violates/i.test(m)) return 'Não foi possível concluir agora. Confira os dados e tente de novo.';
  if (/pgrst|postgrest/i.test(m)) {
    registrarTecnico(e, msg, codigo);
    return MENSAGEM_ERRO_TECNICO;
  }
  // Códigos SQL do Postgres (ex.: 23505, 42501, 42P01) e "column/relation does not exist".
  if (/^[0-9a-z]{5}$/.test(codigo) || /column .* does not exist|relation .* does not exist/i.test(m)) {
    registrarTecnico(e, msg, codigo);
    return MENSAGEM_ERRO_TECNICO;
  }

  // Mensagem em inglês (erro técnico não traduzido): nunca chega crua à tela.
  if (
    /error|failed|invalid|permission|denied|required|not found|already|exception|undefined|null/i.test(
      m,
    ) &&
    /[a-z]{3,}/.test(msg)
  ) {
    registrarTecnico(e, msg, codigo);
    return padrao;
  }

  // Deixa passar a mensagem do banco (já amigável, em português).
  return msg;
}